import { useEffect, useState } from 'react'
import styled, { createGlobalStyle } from 'styled-components'

import { Footer } from 'components/Footer/Footer'
import { Typography } from 'components/Typography/Typography'
import { useAppTheme } from 'contexts/ThemeProviderContext'
import { IconError } from 'components/Icons/IconError'
import { IconCopy } from 'components/Icons/IconCopy'
import { Button } from 'components/Button/Button'
import { useIsMobile } from 'utils/useIsMobile'

const FallbackGlobalStyle = createGlobalStyle`
  body {
    margin: 0 !important;
    background: ${({ theme }) => theme.colors.background.primary};
  }
`

const Wrapper = styled.div`
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    background: ${p => p.theme.colors.gradients.pageBackgroundGradient};
  }
`

const Container = styled.div<{ $scrolled: boolean }>`
  position: fixed;
  // top: 56px;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  padding: 20px 0px;
  justify-content: space-between;
  align-items: center;
  z-index: 10;

  ${p =>
    p.$scrolled &&
    `
    background: ${p.theme.colors.header.scrolled};
    backdrop-filter: blur(25px);
  `}

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 20px;
    width: calc(100% - 40px);
  }
`

const MaxWidthContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin: auto;
`

const LogoImg = styled.img`
  width: 171px:
  height: 26px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 125px;
    height: 19px;
  }
`

const Page = styled.div`
  min-height: calc(100vh - 462px);
  margin-top: 100px;
  box-sizing: border-box;
  text-align: center;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 0 20px 40px;
    margin-top: 80px;
    width: 100%;
    max-width: 100%;
  }

  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
  }

  color: ${p => p.theme.colors.text.primary};

  a {
    color: ${p => p.theme.colors.text.primary};
  }
`

const SupportMessage = styled(Typography)`
  text-align: center;
  margin-top: 16px;

  a {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: underline;
    font-weight: 500;
    cursor: pointer;
  }
`

const ScrollToTop = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return null
}

interface Props {
  eventId: string
}

export const SentryFallbackPage = ({ eventId }: Props) => {
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [scrolled])

  const handleCopyClick = async (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation()
    await navigator.clipboard.writeText(eventId || '')
  }

  const { theme } = useAppTheme()

  const isMobile = useIsMobile()

  return (
    <>
      <FallbackGlobalStyle />
      <ScrollToTop />
      <Wrapper>
        <Container $scrolled={scrolled}>
          <MaxWidthContainer>
            <a href="/" style={{ display: 'flex', alignItems: 'center' }}>
              <LogoImg
                src={theme === 'dark' ? '/logo-title-header-dark.svg' : '/logo-title-header.svg'}
                width="171"
                height="26"
                alt="KAI"
              />
            </a>
          </MaxWidthContainer>
        </Container>
      </Wrapper>
      <Page>
        <IconError />
        <Typography variant={isMobile ? 'subtitle1' : 'title2'} fontWeight="semiBold">
          Oops! Something went wrong
        </Typography>
        <Typography variant="description" style={{ marginTop: 8 }}>
          We're really sorry for the inconvenience this caused. Our team has been notified and is looking into it.
        </Typography>
        <Typography
          variant="description"
          style={{ marginTop: 24, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          Please reference this ID: {eventId}{' '}
          <div style={{ display: 'flex', marginLeft: 8, cursor: 'pointer' }} onClick={handleCopyClick}>
            <IconCopy />
          </div>
        </Typography>
        <SupportMessage variant="description">
          <a href="https://discord.gg/YOUR_INVITE_HERE" target="_blank" rel="noopener noreferrer">
            Report this issue to support on Kai Discord
          </a>
        </SupportMessage>

        <Button onClick={() => window.location.reload()} style={{ marginTop: 32 }}>
          Try reloading the page
        </Button>
      </Page>
      <Footer />
    </>
  )
}
